import classNames from "classnames";
import React from "react";

import { VideoModel } from "@courses-pm2/api";
import { getIcon } from "@courses-pm2/icons";
import { Ribbon } from "@courses-pm2/ribbon-util-component";

import { ContentFade } from "@courses-pm2/video-component/src/ts/components/transitions/ContentFade";
import { urls } from "@courses-pm2/video-component/src/ts/config";

export type VideoComponentProps = {
  /**
   * The model contains all of the content to render within each instance of
   * the component.
   */
  model: VideoModel;
};

type VideoComponentState = {
  /**
   * Specifies whether the iFrame has loaded yet.
   */
  isLoaded?: boolean;
};

/**
 * Root of the render tree for the standard image-component used to render
 * image elements hosted within PM2.
 */
export class VideoComponent extends React.PureComponent<
  VideoComponentProps,
  VideoComponentState
> {
  /**
   * Setup initial state
   */
  public state: VideoComponentState = {};

  /**
   * Primary render method.
   */
  public render(): React.JSX.Element {
    const { model } = this.props;

    // Determine if a topbar will be displayed for this element
    const displayTitle = model.title && !model.displayInternalInfo;
    const displayDescription = model.description && !model.displayInternalInfo;
    const displayCaptionAbove =
      model.displayCaptionAbove &&
      (displayTitle || displayDescription || model.caption);
    const displayCaptionBelow =
      model.displayCaptionBelow &&
      (displayTitle || displayDescription || model.caption);
    const withTopbar = !!model.ribbon || displayCaptionAbove;

    // Determine class name of the wrapper
    const className = classNames("video", {
      "video--withTopbar": withTopbar,
      "video--withBottombar": displayCaptionBelow,
    });

    return (
      <div className={className}>
        {this.renderTop()}

        <div className="video__wrapper">
          {this.renderPreloader()}
          {this.renderVideo()}
        </div>

        {this.renderBottom()}

        <style jsx>{`
          .video {
            /** Add spacing between this component and the next */
            margin-bottom: 30px;

            &.video--withTopbar,
            &.video--withBottombar {
              /**
                             * Render a dropshadow around the whole element, if
                             * it contains a top or a bottom bar
                             */
              box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
            }
          }

          .video__wrapper {
            /** Allow usage of position absolute within */
            position: relative;

            /**
                         * Create 16:9 aspect ratio within wrapper, since video
                         * will always have this aspect
                         */
            padding-bottom: 56.25%;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Rendered that'll display the preloader while audio is being fetched.
   */
  protected renderPreloader(): React.JSX.Element {
    const { isLoaded } = this.state;
    const displayPreloader = !isLoaded;

    return (
      <ContentFade key="preloader" in={displayPreloader}>
        {(ref) => (
          <>
            <div ref={ref} className="preloader">
              <i className="icon">
                {getIcon("gyldendal", { fillColor: "rgba(0, 0, 0, 0.25)" })}
              </i>
            </div>

            <style jsx>{`
              .preloader::before {
                /** Force rendering of preloader */
                content: "";

                /** Fill entire container */
                display: block;
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;

                /* Set a default preloading background color */
                background: rgba(0, 0, 0, 0.1);
              }

              .icon {
                /** Center preload icon within container */
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);

                /** Setup dimensions of icon */
                width: 40px;
                height: 60px;
              }
            `}</style>
          </>
        )}
      </ContentFade>
    );
  }

  /**
   * Renderer that'll render the actual iframe responsible for displaying the
   * iframe
   */
  protected renderVideo(): React.ReactNode {
    const { model } = this.props;
    const { isLoaded } = this.state;

    const url = new URL(
      `${urls.video23Path}/v.ihtml/player.html?source=embed&token=${model.token}&photo%5fid=${model.photoId}&autoPlay=0`,
    );

    if (model.displayInternalInfo) {
      url.searchParams.set("title", model.title || " ");
      url.searchParams.set("description", model.description || " ");
    } else {
      url.searchParams.set("title", " ");
      url.searchParams.set("description", " ");
    }

    // Generate class name to display
    const className = classNames("video__iframe", {
      "video__iframe--loaded": isLoaded,
    });

    // tslint:disable react-iframe-missing-sandbox
    return (
      <iframe
        title={model.title}
        className={className}
        src={url.toString()}
        frameBorder="0"
        scrolling="no"
        allowFullScreen
        allow="autoplay; fullscreen"
        onLoad={this.onIframeLoad}
      >
        <style jsx>{`
          .video__iframe {
            /** Override defaults */
            border: 0;

            /** Extend to fill the wrapping element */
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 2;

            /**
                         * Prepare fade in once the frame has been fully
                         * loaded.
                         */
            opacity: 0;
            transition: opacity 0.25s linear;

            &.video__iframe--loaded {
              opacity: 1;
            }
          }
        `}</style>
      </iframe>
    );
    // tslint:enable react-iframe-missing-sandbox
  }

  /**
   * Renderer that'll inject the bar above the video (if one should be
   * visible).
   */
  protected renderTop(): React.JSX.Element | undefined {
    const { model } = this.props;

    const displayTitle = model.title && !model.displayInternalInfo;
    const displayDescription = model.description && !model.displayInternalInfo;
    const displayCaption =
      (displayTitle || displayDescription || model.caption) &&
      model.displayCaptionAbove;

    // Bail out if topbar shouldn't be displayed (ie. if there is no
    // ribbon or no caption available)
    if (!model.ribbon && !displayCaption) {
      return undefined;
    }

    const className = classNames("video__top", {
      "video__top--withRibbon": !!model.ribbon,
    });

    return (
      <div className={className}>
        {this.renderRibbon()}
        {displayCaption && this.renderCaption()}

        <style jsx>{`
          .video__top {
            /** Overlap the actual video */
            position: relative;
            z-index: 2;

            /**
                         * Force container to fit at least a single line of text
                         * within itself
                         */
            min-height: 1.3em;

            /** Apply box styling */
            background: #fff;
            padding: calc(15px - 0.15em) 15px;

            /**
                         * Render shadow on top of the video using pseudo after
                         * element and css gradient.
                         */
            &::after {
              /** Flow right under the topbar */
              position: absolute;
              top: 100%;
              left: 0;
              right: 0;

              /** Apply dropshadow */
              height: 6px;
              background: linear-gradient(
                to bottom,
                rgba(0, 0, 0, 0.06),
                rgba(0, 0, 0, 0)
              );

              /** Force rendering of pseudo element */
              content: "";
            }
          }

          .video__top--withRibbon {
            /**
                         * Ribbons will overlap part of the left side of the bar
                         * so we need to adjust padding accordingly!
                         */
            padding-left: 60px;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll inject the bar below the video (if one should be
   * visible).
   */
  protected renderBottom(): React.JSX.Element | undefined {
    const { model } = this.props;

    const displayTitle = model.title && !model.displayInternalInfo;
    const displayDescription = model.description && !model.displayInternalInfo;
    const displayCaption = displayTitle || displayDescription || model.caption;

    // Bail out if caption shouldn't be displayed below the video
    if (!displayCaption || !model.displayCaptionBelow) {
      return undefined;
    }

    // ... Otherwise render the caption here!
    return (
      <div className="video__bottom">
        {this.renderCaption()}

        <style jsx>{`
          .video__bottom {
            /** Apply box styling */
            padding: calc(15px - 0.15em) 15px;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Helper that'll render the <Ribbon /> utility to inject the icon on the
   * top-left side of the component.
   */
  protected renderRibbon(): React.JSX.Element | undefined {
    if (!this.props.model.ribbon) {
      return undefined;
    }

    return <Ribbon iconLetter={this.props.model.ribbon} />;
  }

  /**
   * Renderer that'll inject the caption associated with the audio, either in
   * the top or bottom section.
   */
  protected renderCaption(): React.JSX.Element {
    const { model } = this.props;

    const displayTitle = model.title && !model.displayInternalInfo;
    const displayDescription = model.description && !model.displayInternalInfo;

    // tslint:disable react-no-dangerous-html
    return (
      <React.Fragment>
        {displayTitle && (
          <span className="captionElement title">{model.renderTitle()}</span>
        )}
        {displayDescription && (
          <span className="captionElement description">
            {model.renderDescription()}
          </span>
        )}
        {model.caption && (
          <span className="captionElement caption">
            {model.renderCaption()}
          </span>
        )}

        <style jsx>{`
          .captionElement {
            /** Apply font styling */
            line-height: 1.3em;

            /** Apply custom styling to different elements */
            &.title {
              font-weight: bold;
            }

            &.description,
            &.caption {
              font-style: italic;
            }

            /**
                         * Ensure elements are visually separated by inserting a
                         * space in between each of them.
                         */
            &::after {
              content: " ";
            }
          }
        `}</style>
      </React.Fragment>
    );
  }

  /**
   * Listener that's triggered once the iframe containing the player has been
   * fully loaded.
   */
  protected onIframeLoad = () => {
    this.setState({ isLoaded: true });
  };
}
